import { useTranslation } from 'react-i18next'
import { formatSellingPrice } from '@boutiklic/shared'
import type { Product } from '../../../types'

const QUICK_AMOUNTS = [1000, 2000, 5000, 10000, 20000, 50000]

interface VenteRapideDesktopProps {
  /** Précision des prix de vente unitaires (0 à 2 décimales). */
  sellingPriceDecimals?: number
  amount: string
  setAmount: (v: string) => void
  onAddQuickAmount: () => void
  frequentProducts: Product[]
  onAddProduct: (product: Product) => void
  currency: string
}

/** W33 — montant libre au clavier physique (pas de pavé tactile, contrairement à B1 mobile), plus
 *  raccourcis de montants et articles fréquents en pastilles ; le panneau de droite (panier +
 *  paiement) reste celui de CaisseDesktop, partagé entre les deux modes (règle de traduction n°7 :
 *  un seul état de vente, pas deux écrans qui divergent). */
export default function VenteRapideDesktop({ amount, setAmount, onAddQuickAmount, frequentProducts, onAddProduct, currency, sellingPriceDecimals = 0 }: VenteRapideDesktopProps) {
  const { t } = useTranslation()

  return (
    <div className="flex-1 min-h-0 overflow-y-auto px-4 2xl:px-6 pb-4 2xl:pb-6 flex flex-col gap-5">
      <div className="p-6 rounded-2xl bg-primary flex flex-col gap-3" style={{ backgroundImage: 'repeating-linear-gradient(45deg,rgba(255,253,250,.05) 0 3px,transparent 3px 14px)' }}>
        <div className="flex items-center justify-between">
          <span className="text-xs font-extrabold tracking-widest text-papier/60">{t('registerSales.saleAmountLabel')}</span>
          <span className="px-2.5 py-1 rounded-lg bg-papier/15 text-papier text-xs font-extrabold">{currency}</span>
        </div>
        <input
          value={amount}
          onChange={(e) => setAmount(e.target.value.replace(/[^0-9,]/g, ''))}
          placeholder="0"
          inputMode="decimal"
          autoFocus
          className="amount w-full bg-transparent outline-none text-papier text-[56px] font-extrabold tracking-tight leading-none"
          style={{ minHeight: 132 }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && amount) onAddQuickAmount()
          }}
        />
        <button
          type="button"
          onClick={onAddQuickAmount}
          disabled={!amount}
          className="self-start px-4 h-11 rounded-xl bg-warning text-encre text-sm font-extrabold disabled:opacity-40"
        >
          {t('registerSales.addAmount', { amount: amount || '0' })}
        </button>
      </div>

      <div className="flex flex-col gap-2.5">
        <div className="text-xs font-extrabold tracking-widest text-text-dim">{t('registerSales.quickAmountsLabel')}</div>
        <div className="flex gap-2 flex-wrap">
          {QUICK_AMOUNTS.map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setAmount(String(v))}
              className="amount px-4 h-10 rounded-xl border border-border-strong bg-bg-card text-sm font-bold hover:border-primary"
            >
              {v.toLocaleString('fr-FR')}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-2.5">
        <div className="text-xs font-extrabold tracking-widest text-text-dim">{t('registerSales.frequentProductsLabel')}</div>
        {frequentProducts.length === 0 ? (
          <div className="text-sm font-semibold text-text-dim">{t('registerSales.noProductMatch')}</div>
        ) : (
          <div className="flex gap-2 flex-wrap">
            {frequentProducts.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => p.quantity > 0 && onAddProduct(p)}
                disabled={p.quantity === 0}
                className="flex items-center gap-2 px-3.5 h-11 rounded-full border border-border-strong bg-bg-card text-sm font-bold disabled:opacity-40 hover:border-primary"
              >
                <span className="truncate max-w-[140px]">{p.name}</span>
                <span className="amount text-xs text-text-dim">{formatSellingPrice(p.salePrice, 'fr', sellingPriceDecimals)}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
